import { AfterViewInit, Component, computed, effect, inject, linkedSignal, OnDestroy, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UserTrackingService } from '@myarvato/core/services';
import { NgbDropdown, NgbDropdownModule, NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { MatomoTrackClickDirective } from 'ngx-matomo-client';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ViewDataService, ViewDialogService } from './services';

@Component({
  selector: 'myarvato-view-manager',
  imports: [
    Message,
    TranslatePipe,
    FormsModule,
    MatomoTrackClickDirective,
    NgbDropdownModule,
    NgbPopoverModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    FloatLabelModule,
    ScrollPanelModule,
  ],
  templateUrl: './view-manager.component.html',
  styleUrl: './view-manager.component.scss',
})
export class ViewManagerComponent implements AfterViewInit, OnDestroy {
  //Injections
  protected readonly viewDataService = inject(ViewDataService);
  protected readonly viewDialogService = inject(ViewDialogService);
  private readonly UserTrackingEventService = inject(UserTrackingService);

  //Fields
  private documentClickHandler: ((event: Event) => void) | null = null;
  protected currentPopover: NgbPopover | null = null;
  protected readonly matomoTrackerCategory: string = 'Transport - Tracking List - View Manager';

  // Signal Queries
  private readonly viewsDropdown = viewChild<NgbDropdown>('viewsDropdown');

  //Change detection
  protected readonly selectedView = linkedSignal(() => this.viewDataService.selectedViewId());
  protected readonly standardViews = computed(() => this.viewDataService.standardViews());
  protected readonly userViews = computed(() => this.viewDataService.userViews());
  protected readonly selectedViewData = computed(() => this.viewDataService.selectedView());
  protected readonly isAdmin = computed(() => this.viewDataService.isAdmin());

  protected readonly dialogActionText = computed(() => this.viewDialogService.dialogActionText());
  protected readonly shouldShowConflictMessage = computed(() => this.viewDialogService.shouldShowConflictMessage());
  protected readonly conflictMessageText = computed(() => this.viewDialogService.conflictMessageText());
  protected readonly canShowCheckbox = computed(() => this.viewDialogService.canShowCheckbox());

  protected readonly saveViewDialogVisible = this.viewDialogService.saveViewDialogVisible;
  protected readonly newViewName = this.viewDialogService.newViewName;
  protected readonly originalViewName = this.viewDialogService.originalViewName;
  protected readonly isStandardView = this.viewDialogService.isStandardView;
  protected readonly viewToDelete = this.viewDialogService.viewToDelete;

  protected readonly isProgrammaticUpdate = this.viewDialogService.isProgrammaticUpdate;

  //effects
  private initializeDialogStateEffect = effect(() => {
    this.viewDialogService.initializeState(
      this.newViewName(),
      this.originalViewName(),
      this.isStandardView(),
      this.selectedViewData(),
      this.standardViews(),
      this.userViews(),
      this.isAdmin(),
    );
  });

  private closeDropdownEffect = effect(() => {
    if (this.viewDialogService.closeDropdownRequested()) {
      const dropdown = this.viewsDropdown();
      if (dropdown && dropdown.isOpen()) {
        dropdown.close();
      }
      this.viewDialogService.resetDropdownCloseRequest();
    }
  });

  ngAfterViewInit(): void {
    this.setupDropdownCloseHandler();
  }

  ngOnDestroy(): void {
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler, { capture: true });
    }
  }

  protected updateViewName(name: string): void {
    this.viewDialogService.newViewName.set(name);
    this.viewDialogService.setCurrentName(name);
  }

  protected updateIsStandardView(checked: boolean): void {
    this.viewDialogService.isStandardView.set(checked);
    this.viewDialogService.setIsStandardViewChecked(checked);
  }

  protected onDialogVisibilityChange(visible: boolean): void {
    if (!visible) {
      this.cancelSaveView();
    }
  }

  protected cancelSaveView(): void {
    this.viewDialogService.closeSaveViewDialog();
    this.viewDialogService.reset();
  }

  protected confirmSaveView(): void {
    const viewName = this.viewDialogService.newViewName().trim();
    if (!viewName) {
      return;
    }

    this.viewDialogService.requestSaveView();
  }

  protected selectView(viewId: string): void {
    this.viewDialogService.requestViewSelection(viewId);
  }

  protected deleteView(viewId: string): void {
    this.viewDialogService.requestViewDeletion(viewId);
  }

  protected getButtonText(): string {
    const selectedViewId = this.selectedView();
    const selectedView = this.viewDataService.getUserView(selectedViewId || '');
    return selectedView?.name || 'Select View';
  }

  protected showDeleteConfirmation(viewId: string, popover: NgbPopover): void {
    if (this.currentPopover && this.currentPopover.isOpen()) {
      this.currentPopover.close();
      setTimeout(() => {
        this.openDeletePopover(viewId, popover);
      }, 100);
    } else {
      this.openDeletePopover(viewId, popover);
    }
  }

  private openDeletePopover(viewId: string, popover: NgbPopover): void {
    this.viewDialogService.setViewToDelete(viewId);
    this.currentPopover = popover;
    popover.open();
  }

  protected confirmDeleteView(): void {
    const viewId = this.viewDialogService.viewToDelete();
    if (viewId && this.currentPopover && this.currentPopover.isOpen()) {
      this.deleteView(viewId);
      this.closeCurrentPopover();
    }
  }

  protected cancelDeleteView(): void {
    if (this.currentPopover && this.currentPopover.isOpen()) {
      this.closeCurrentPopover();
    }
  }

  private closeCurrentPopover(): void {
    if (this.currentPopover && this.currentPopover.isOpen()) {
      this.currentPopover.close();
    }
    setTimeout(() => {
      this.currentPopover = null;
      this.viewDialogService.resetDeleteState();
    }, 50);
  }

  protected onPopoverHidden(): void {
    setTimeout(() => {
      if (!this.currentPopover || !this.currentPopover.isOpen()) {
        this.viewDialogService.resetDeleteState();
        this.currentPopover = null;
      }
    }, 50);
  }

  private setupDropdownCloseHandler(): void {
    this.documentClickHandler = (event: Event) => {
      const dropdown = this.viewsDropdown();
      if (!dropdown || !dropdown.isOpen()) {
        return;
      }

      const target = event.target as Element;
      const isPopoverClick = target.closest('.delete-view-popover') !== null;
      const dropdownElement = document.querySelector('.viewsDropdownMenu');
      const isDropdownClick = dropdownElement && dropdownElement.contains(target);
      const toggleButton = document.querySelector('[ngbDropdownToggle]');
      const isToggleClick = toggleButton && toggleButton.contains(target);

      if (!isDropdownClick && !isToggleClick && !isPopoverClick) {
        dropdown.close();
      }
    };

    document.addEventListener('click', this.documentClickHandler, { capture: true });
  }
}
